import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaExclamationTriangle } from 'react-icons/fa';

/**
 * Componente de select personalizado con diseño propio pero que usa
 * el sistema nativo de opciones para móvil
 */
const CustomSelect = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "Seleccionar", 
  disabled = false,
  className = '',
  name = '',
  required = false,
  requiredMessage = 'Este campo es requerido',
  forceShowValidation = false // Nueva prop para forzar mostrar validación
}) => {
  // Estado interno para forzar re-render en iOS
  const [displayValue, setDisplayValue] = useState(value);
  // Estado para manejo de validación
  const [hasBeenTouched, setHasBeenTouched] = useState(false);
  
  // Efecto para sincronizar el valor interno con el prop value
  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  // Efecto para manejar forceShowValidation
  useEffect(() => {
    if (forceShowValidation) {
      setHasBeenTouched(true);
    }
  }, [forceShowValidation]);

  // Determinar si mostrar error
  const showError = (hasBeenTouched || forceShowValidation) && required && (!value || value === '' || value === '0');
  
  // Procesar las opciones para asegurar que tengan el formato correcto
  const processedOptions = options.map(option => {
    // Normalizar la estructura de la opción
    return {
      id: option.id || option.value || '',
      label: option.label || option.Nombre || option.nombre || option.text || option.name || '',
      value: option.id || option.value || ''
    };
  });
  // Obtener la etiqueta seleccionada actualmente
  const getSelectedLabel = () => {
    const currentValue = displayValue || value;
    if (!currentValue) return placeholder;
    
    // Buscar en las opciones procesadas para mayor consistencia
    const selectedOption = processedOptions.find(opt => 
      String(opt.value) === String(currentValue) || String(opt.id) === String(currentValue)
    );
    
    if (!selectedOption) {
      // Fallback: buscar en opciones originales
      const originalOption = options.find(opt => 
        String(opt.id) === String(currentValue) || 
        String(opt.value) === String(currentValue) ||
        String(opt.Nombre) === String(currentValue) ||
        String(opt.nombre) === String(currentValue)
      );
      
      if (originalOption) {
        return originalOption.label || 
               originalOption.Nombre || 
               originalOption.nombre || 
               originalOption.text || 
               originalOption.name || 
               placeholder;
      }
      
      return placeholder;
    }
    
    return selectedOption.label || placeholder;
  };
  
  // Manejar el cambio en el select nativo
  const handleChange = (e) => {
    const selectedValue = e.target.value;
    
    // Actualizar el estado interno inmediatamente para iOS
    setDisplayValue(selectedValue);
    
    // Siempre pasar un evento sintético completo para consistencia
    if (onChange && typeof onChange === 'function') {
      onChange({
        target: {
          name: name || 'select',
          value: selectedValue,
          type: 'select-one'
        },
        preventDefault: () => {},
        stopPropagation: () => {}
      });
    }
    
  };

  // Manejar cuando el select pierde el foco (para validación)
  const handleBlur = () => {
    setHasBeenTouched(true);
  };

  // Manejar validación inválida (prevenir tooltips del navegador)
  const handleInvalid = (e) => {
    e.preventDefault();
    setHasBeenTouched(true);
  };

  // Determinar clases CSS según el estado de validación
  const getSelectClasses = () => {
    let classes = `relative h-10 bg-gray-700 border rounded-lg ${className}`;
    
    if (showError) {
      classes += ' border-red-500';
    } else {
      classes += ' border-gray-500';
    }
    
    return classes;
  };
  
  return (
    <div>
      <div className={getSelectClasses()}>
        {/* Select nativo con estilo transparente */}
        <select
          name={name}
          value={displayValue || ''}
          onChange={handleChange}
          onBlur={handleBlur}
          onInvalid={handleInvalid}
          disabled={disabled}
          required={required}
          aria-label={placeholder}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 appearance-none"
        >
          <option value="">{placeholder}</option>
          {processedOptions.map((option) => (
            <option key={option.value || option.id} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Capa visual con el texto seleccionado */}
        <div className="flex items-center justify-between h-full pointer-events-none px-3">
          <span className={displayValue ? 'text-white' : 'text-gray-400'}>{getSelectedLabel()}</span>
          <FaChevronDown className="text-gray-400" />
        </div>
      </div>
      
      {/* Mensaje de error personalizado */}
      {showError && (
        <p className="validation-message">
          <FaExclamationTriangle />
          {requiredMessage}
        </p>
      )}
    </div>
  );
};

export default CustomSelect;
